import { ReactNode } from 'react';
import { useLang } from 'hooks/lang';

import { calculatePackageAmountToBecomeBalance, invoiceConfigByCode, actions } from 'entities';

import {
  Alert,
  Button,
  Column,
  Modal,
  Row,
  Tag,
  DropdownMenuItem,
  LabeledTextarea,
  LabeledSelect,
} from 'components/Blocks';
import { MultiCoinIcon } from 'components/Icons';

import { useFormControlV2 } from 'hooks';
import { useSingleConfirmation } from 'providers/Confirmation';
import { usePackage } from '../context';

import { getFirst, toUniqueList } from 'utils/array';
import { formatCurrency } from 'utils/text';
import { pick } from 'utils/object';

interface ConvertToBalanceModalProps {
  children: (props: { open: () => void }) => ReactNode;
}

// This is done like this as having the modal inside the dropdown menu would
// automatically close it due to the buble effect. As we are not sure how we want to procede
// with the logic from here, we'll adapt until we decide

export function ConvertToBalanceModal({ children }: ConvertToBalanceModalProps) {
  const { t } = useLang();

  const { data, reload } = usePackage();

  const countryOptions = toUniqueList(
    data.invoices
      .filter(({ status }) => invoiceConfigByCode[status].countToPackageBalanceConversion)
      .map((i) => i.country),
  );

  const [state, { dispatch, handleChange, resetDefault }] = useFormControlV2({
    open: false,
    country: countryOptions.length > 1 ? '' : getFirst(countryOptions),
    justification: '',
  });

  const canSubmit = !!(state.country && state.justification.trim());

  const convertToBalance = actions.invoice.convertPackageToBalance.use({
    block: !canSubmit,

    params: {
      packageId: data.id,
      ...pick(state, ['country', 'justification']),
    },

    onSuccess: () => {
      resetDefault();
      reload();
    },
  });

  const confirmBalance = useSingleConfirmation({
    onConfirm: convertToBalance.onClick,
    title: t('confirm-balance-conversion')!,
    type: 'irreversible',
    text: (
      <p>
        <span className="text-success">
          {formatCurrency(calculatePackageAmountToBecomeBalance(data), data.currency)}
        </span>

        <span>{t('will-be-converted-to-balance')}</span>
      </p>
    ),
  });

  return (
    <>
      <Modal
        title={t('convert-to-balance')!}
        isOpen={state.open}
        close={resetDefault}
        width="min(80vw, 660px)"
        gap="6"
      >
        <Alert title={t('package-conversion-alert-title')} level="info">
          {t('package-conversion-alert-description')}
        </Alert>

        <Row justifyContent="space-between">
          <span>{t('balance-value')}</span>

          <Tag
            name={formatCurrency(calculatePackageAmountToBecomeBalance(data), data.currency)}
            color="success-light"
            round={false}
            size="medium"
          />
        </Row>

        <Column gap="2">
          <LabeledSelect
            id="country"
            label={t('country')!}
            disabled={countryOptions.length === 1}
            value={state.country}
            onChange={handleChange}
            labelKey="country"
            valueKey="country"
            options={countryOptions.map((country) => ({
              country,
            }))}
          />

          <span className="text-xs">{t('package-conversion-country-info')}</span>
        </Column>

        <LabeledTextarea
          name="justification"
          label={t('justification')!}
          value={state.justification}
          onChange={handleChange}
        />

        <Button variant="outlined" disabled={!canSubmit} onClick={confirmBalance}>
          <MultiCoinIcon />
          {t('convert')}
        </Button>
      </Modal>

      {children({
        open: () => dispatch({ open: true }),
      })}
    </>
  );
}

export function ConvertFromBalanceMenuItem({ open }: { open: () => void }) {
  const { t } = useLang();

  return (
    <DropdownMenuItem onClick={open}>
      <MultiCoinIcon />
      {t('convert-to-balance')}
    </DropdownMenuItem>
  );
}
